// Log payloads end up in Loki. A MediaStreamTrack carries a `label` that, for a
// screen share, is the shared window or screen title (PII), and its settings
// carry a deviceId. Replace tracks and streams with a whitelisted summary
// before anything is handed to a logger.

const MAX_ERROR_MESSAGE_LENGTH = 200;

export interface TrackLogSummary {
  kind: string;
  readyState: string;
  muted: boolean;
  enabled: boolean;
}

function isTrackLike(value: object): value is MediaStreamTrack {
  if (
    typeof MediaStreamTrack !== "undefined" &&
    value instanceof MediaStreamTrack
  ) {
    return true;
  }

  const track = value as Partial<MediaStreamTrack>;
  return (
    typeof track.kind === "string" &&
    "readyState" in track &&
    ("label" in track || typeof track.getSettings === "function")
  );
}

function isStreamLike(value: object): value is MediaStream {
  if (typeof MediaStream !== "undefined" && value instanceof MediaStream) {
    return true;
  }

  return typeof (value as Partial<MediaStream>).getTracks === "function";
}

export function trackLogSummary(track: MediaStreamTrack): TrackLogSummary {
  return {
    kind: track.kind,
    readyState: track.readyState,
    muted: track.muted,
    enabled: track.enabled
  };
}

// Screen settings that help to explain a black share, nothing identifying:
// no deviceId, no groupId, no label.
export function screenTrackSettingsSummary(track: MediaStreamTrack) {
  const settings =
    typeof track.getSettings === "function" ? track.getSettings() : {};
  const { displaySurface } = settings as { displaySurface?: string };

  return {
    displaySurface,
    width: settings.width,
    height: settings.height,
    frameRate: settings.frameRate
  };
}

export function errorLogSummary(error: unknown) {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message.slice(0, MAX_ERROR_MESSAGE_LENGTH)
    };
  }

  return {
    errorName: typeof error,
    errorMessage: String(error).slice(0, MAX_ERROR_MESSAGE_LENGTH)
  };
}

export function sanitizeLogArg(arg: unknown): unknown {
  if (!arg || typeof arg !== "object") {
    return arg;
  }

  try {
    if (isTrackLike(arg)) {
      return { track: trackLogSummary(arg) };
    }

    if (isStreamLike(arg)) {
      return {
        stream: { tracks: arg.getTracks().map((_) => trackLogSummary(_)) }
      };
    }
  } catch {
    return { unloggable: true };
  }

  return arg;
}

export function sanitizeLogArgs(args: unknown[]): unknown[] {
  return args.map((_) => sanitizeLogArg(_));
}
